import { useMemo } from "react";
import type { ArchitecturalMetrics } from "src/types/ArchitecturalMetrics";
import { MathSum } from "src/utils/MathSum";
import { sqmToSqft } from "../../../hooks/meterToFeet";
import { Table } from "../Table";
import { type Unit } from "../UnitSelect";
import { useTranslation } from "src/translations";

interface Row {
  label: string;
  avgArea: number;
  quantity: number;
  mix: number;
}

const UNIT_TYPE_KEYS: Record<string, string> = {
  "1B2P": "unitType.1b2p",
  "2B3P": "unitType.2b3p",
  "2B4P": "unitType.2b4p",
  "3B5P": "unitType.3b5p",
  "3B6P": "unitType.3b6p",
};

type Translator = (key: string, vars?: Record<string, string | number>) => string;

export function UnitMixTable(props: { metrics: ArchitecturalMetrics; unit: Unit }) {
  const { t } = useTranslation();
  const { rows, totalRow } = useRows(props.metrics, props.unit, t);
  return (
    <div>
      <Table.Sticky>
        <Table.Head label={t("metrics.unitMix")} variant="section" />

        <Table.Row>
          <Table.Head label={t("metrics.type")} variant="header" position="start" />
          <Table.Head
            label={t("metrics.avgArea")}
            variant="header"
            position="end"
            symbol={props.unit}
          />
          <Table.Head label={t("metrics.quantity")} variant="header" position="end" />
          <Table.Head label={t("metrics.mix")} variant="header" position="end" />
        </Table.Row>
      </Table.Sticky>
      {rows.map((row) => (
        <TableRow key={row.label} row={row} variant="cell" />
      ))}
      <TableRow row={totalRow} variant="total" />
    </div>
  );
}

function TableRow(props: { row: Row; variant: "cell" | "total" }) {
  return (
    <Table.Row key={props.row.label}>
      <Table.Col>
        <Table.Label label={props.row.label} variant={props.variant} />
      </Table.Col>
      <Table.Col>
        <Table.Cell value={props.row.avgArea} precision={2} variant={props.variant} />
      </Table.Col>
      <Table.Col>
        <Table.Cell value={props.row.quantity} variant={props.variant} />
      </Table.Col>
      <Table.Col>
        <Table.Cell value={props.row.mix} precision={1} variant={props.variant} symbol="%" />
      </Table.Col>
    </Table.Row>
  );
}

function useRows(
  metrics: ArchitecturalMetrics,
  unit: Unit,
  t: Translator
): { rows: Row[]; totalRow: Row } {
  return useMemo(() => {
    const rows: Row[] = metrics.UnitCategoryMixPerCategory.map((mix) => {
      const avgArea = mix.averageNia;

      return {
        label: UNIT_TYPE_KEYS[mix.name] ? t(UNIT_TYPE_KEYS[mix.name]) : mix.name,
        avgArea: unit === "sqm" ? avgArea : sqmToSqft(avgArea),
        quantity: mix.count,
        mix: mix.ratio * 100,
      };
    }).sort((a, b) => a.label.localeCompare(b.label));

    const quantity = MathSum(metrics.UnitCategoryMixPerCategory.map((mix) => mix.count));
    const avgArea =
      MathSum(metrics.UnitCategoryMixPerCategory.map((mix) => mix.averageNia * mix.count)) /
      quantity;

    const totalRow: Row = {
      label: t("common.total"),
      avgArea: unit === "sqm" ? avgArea : sqmToSqft(avgArea),
      quantity,
      mix: MathSum(metrics.UnitCategoryMixPerCategory.map((mix) => mix.ratio)) * 100,
    };

    return {
      rows,
      totalRow,
    };
  }, [metrics.UnitCategoryMixPerCategory, unit, t]);
}
